import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Typography,
  Button,
  Layout,
  Input,
  Space,
  message,
  Spin,
  Tooltip,
  Modal,
  Drawer,
  Grid,
} from "antd";
import { createStyles } from "antd-style";
import {
  HomeOutlined,
  SearchOutlined,
  EyeOutlined,
  FilterOutlined,
  ReloadOutlined,
  MenuOutlined,
  MedicineBoxOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import type { ColumnsType } from "antd/es/table";

const { Title } = Typography;
const { Content } = Layout;
const { Search } = Input;
const { useBreakpoint } = Grid;

// 定义患者报告数据接口
interface PatientReport {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string | null;
  UserId: number;
  PatientName: string;
  PatientReport: string;
  ChatHistory: string;
}

// 定义API响应接口
interface ApiResponse {
  status: number;
  data?: {
    inquiries: PatientReport[];
    is_doctor: boolean;
  };
  msg?: string;
}

const useStyles = createStyles(({ token }) => {
  return {
    container: {
      minHeight: "100vh",
      background: token.colorBgLayout,
    },
    header: {
      marginBottom: "24px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
    },
    headerTitle: {
      margin: 0,
      fontSize: "24px",
      "@media (max-width: 576px)": {
        fontSize: "20px",
        marginBottom: "12px",
        width: "100%",
      },
    },
    card: {
      marginBottom: "24px",
      boxShadow: token.boxShadowTertiary,
    },
    searchBar: {
      marginBottom: "24px",
      display: "flex",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "12px",
      "@media (max-width: 576px)": {
        flexDirection: "column",
      },
    },
    searchGroup: {
      display: "flex",
      flexWrap: "wrap",
      gap: "8px",
      "@media (max-width: 576px)": {
        width: "100%",
      },
    },
    backButton: {
      marginRight: "8px",
    },
    content: {
      padding: "24px",
      background: token.colorBgLayout,
      minHeight: "100vh",
      "@media (max-width: 576px)": {
        padding: "12px",
      },
    },
    reportText: {
      maxWidth: "400px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      "@media (max-width: 768px)": {
        maxWidth: "200px",
      },
    },
    mobileMenuButton: {
      display: "none",
      "@media (max-width: 768px)": {
        display: "block",
        marginRight: "12px",
      },
    },
    mobileHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "16px",
      padding: "12px 16px",
      background: token.colorBgContainer,
      boxShadow: token.boxShadow,
      borderRadius: token.borderRadius,
      "@media (max-width: 576px)": {
        flexWrap: "wrap",
      },
    },
    logo: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "18px",
      fontWeight: "bold",
      color: token.colorPrimary,
      "& img": {
        width: "32px",
        height: "32px",
      },
    },
    drawerMenu: {
      "& .ant-drawer-body": {
        padding: 0,
      },
    },
  };
});

const DoctorPage: React.FC = () => {
  const { styles } = useStyles();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [loading, setLoading] = useState(false);
  const [patientReports, setPatientReports] = useState<PatientReport[]>([]);
  const [searchText, setSearchText] = useState("");
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [currentReport, setCurrentReport] = useState<PatientReport | null>(
    null
  );
  const [drawerVisible, setDrawerVisible] = useState(false);

  const isMobile = !screens.md;

  // 获取患者报告数据
  const fetchPatientReports = async () => {
    setLoading(true);
    try {
      // 获取医生的token
      const token = localStorage.getItem('dakang_token');
      if (!token) {
        message.error('未登录，请先登录');
        navigate('/login');
        return;
      }

      const response = await fetch("https://dk-backend.ibase.site/api/v1/getinquirylist", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          `API请求失败: ${response.status} ${response.statusText}`
        );
      }

      const result: ApiResponse = await response.json();

      if (result.status === 200 && result.data) {
        const { inquiries, is_doctor } = result.data;
        
        if (!is_doctor) {
          message.error("您不是医生，无权访问此页面");
          navigate('/');
          return;
        }

        if (Array.isArray(inquiries)) {
          setPatientReports(inquiries);
          message.success(`成功获取 ${inquiries.length} 条患者报告`);
        } else {
          message.warning("未找到患者报告数据");
        }
      } else {
        message.error(result.msg || "获取数据失败");
      }
    } catch (error) {
      console.error("获取患者报告失败:", error);
      message.error("获取患者报告失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  };

  // 组件加载时获取数据
  useEffect(() => {
    fetchPatientReports();
  }, []);

  // 查看报告详情
  const handleViewReport = (record: PatientReport) => {
    setCurrentReport(record);
    setViewModalVisible(true);
  };

  // 搜索患者
  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  // 过滤数据
  const filteredReports = patientReports.filter(
    (report) =>
      report.PatientName.toLowerCase().includes(searchText.toLowerCase()) ||
      report.PatientReport.toLowerCase().includes(searchText.toLowerCase()) ||
      (report.ChatHistory &&
        report.ChatHistory.toLowerCase().includes(searchText.toLowerCase()))
  );

  // 格式化日期时间
  const formatDateTime = (dateTimeStr: string) => {
    try {
      // 处理 ISO 8601 格式
      const date = new Date(dateTimeStr);
      if (isNaN(date.getTime())) {
        return dateTimeStr;
      }
      // 格式化为 YYYY-MM-DD HH:mm:ss
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).replace(/\//g, '-');
    } catch (error) {
      return dateTimeStr;
    }
  };

  // 表格列定义
  const getColumns = (): ColumnsType<PatientReport> => {
    // 移动端简化列
    if (isMobile) {
      return [
        {
          title: "患者姓名",
          dataIndex: "PatientName",
          key: "PatientName",
          render: (text) => {return text},
        },
        {
          title: "创建时间",
          dataIndex: "CreatedAt",
          key: "CreatedAt",
          render: (text) => formatDateTime(text),
        },
        {
          title: "操作",
          key: "action",
          render: (_, record) => (
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewReport(record)}
            >
              查看
            </Button>
          ),
        },
      ];
    }

    // 桌面端完整列
    return [
      {
        title: "记录ID",
        dataIndex: "ID",
        key: "ID",
        width: 100,
      },
      {
        title: "患者姓名",
        dataIndex: "PatientName",
        key: "PatientName",
        width: 120,
        render: (text) => {return text},
      },
      {
        title: "问诊报告",
        dataIndex: "PatientReport",
        key: "PatientReport",
        render: (text) => (
          <Tooltip title={text}>
            <div className={styles.reportText}>{text}</div>
          </Tooltip>
        ),
      },
      {
        title: "对话历史",
        dataIndex: "ChatHistory",
        key: "ChatHistory",
        render: (text) => (
          <Tooltip title={text || '无'}>
            <div className={styles.reportText}>{text || '无'}</div>
          </Tooltip>
        ),
      },
      {
        title: "创建时间",
        dataIndex: "CreatedAt",
        key: "CreatedAt",
        width: 180,
        render: (text) => formatDateTime(text),
        sorter: (a, b) =>
          new Date(a.CreatedAt).getTime() - new Date(b.CreatedAt).getTime(),
      },
      {
        title: "操作",
        key: "action",
        width: 120,
        render: (_, record) => (
          <Space size="middle">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewReport(record)}
            >
              查看
            </Button>
          </Space>
        ),
      },
    ];
  };

  return (
    <Layout className={styles.container}>
      {/* 移动端抽屉菜单 */}
      {isMobile && (
        <Drawer
          placement="left"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          width={220}
          className={styles.drawerMenu}
          title={
            <div className={styles.logo}>
              <img src="./dakanglogo.png" alt="达康AI医生" />
              <span>达康AI医生</span>
            </div>
          }
        >
          <Space direction="vertical" style={{ width: "100%", padding: "16px" }}>
            <Button
              type="primary"
              icon={<HomeOutlined />}
              onClick={() => navigate("/")}
              block
            >
              返回主页
            </Button>
          </Space>
        </Drawer>
      )}

      <Content className={styles.content}>
        {/* 移动端顶部导航 */}
        {isMobile && (
          <div className={styles.mobileHeader}>
            <Button
              icon={<MenuOutlined />}
              onClick={() => setDrawerVisible(true)}
              className={styles.mobileMenuButton}
            />
            <div className={styles.logo}>
              <img src="./dakanglogo.png" alt="达康AI医生" />
              <span>达康AI医生</span>
            </div>
          </div>
        )}

        <div className={styles.header}>
          <Title level={isMobile ? 4 : 2} className={styles.headerTitle}>
            <MedicineBoxOutlined style={{ marginRight: "8px" }} />
            患者问诊报告管理
          </Title>
          {!isMobile && (
            <Button
              type="primary"
              icon={<HomeOutlined />}
              onClick={() => navigate("/")}
              className={styles.backButton}
            >
              返回主页
            </Button>
          )}
        </div>

        <Card className={styles.card}>
          <div className={styles.searchBar}>
            <div className={styles.searchGroup}>
              <Search
                placeholder="搜索患者姓名或报告内容"
                allowClear
                enterButton={
                  <>
                    <SearchOutlined /> 搜索
                  </>
                }
                size={isMobile ? "small" : "middle"}
                onSearch={handleSearch}
                style={{ width: isMobile ? "100%" : 300 }}
              />
              <Button icon={<FilterOutlined />} size={isMobile ? "small" : "middle"}>
                筛选
              </Button>
            </div>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={fetchPatientReports}
              loading={loading}
              size={isMobile ? "small" : "middle"}
            >
              刷新数据
            </Button>
          </div>

          <Spin spinning={loading}>
            <Table
              columns={getColumns()}
              dataSource={filteredReports}
              rowKey="ID"
              pagination={{
                pageSize: isMobile ? 5 : 10,
                showSizeChanger: !isMobile,
                showTotal: (total) => `共 ${total} 条记录`,
                size: isMobile ? "small" : "default",
              }}
              size={isMobile ? "small" : "middle"}
              scroll={isMobile ? { x: true } : undefined}
            />
          </Spin>
        </Card>
      </Content>

      {/* 查看报告详情的模态框 */}
      <Modal
        title={`患者报告详情 - ${currentReport?.PatientName || ""}`}
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setViewModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={isMobile ? "95%" : 700}
      >
        {currentReport && (
          <Card>
            <p>
              <strong>记录ID:</strong> {currentReport.ID}
            </p>
            <p>
              <strong>患者姓名:</strong> {currentReport.PatientName}
            </p>
            <p>
              <strong>用户ID:</strong> {currentReport.UserId}
            </p>
            <p>
              <strong>创建时间:</strong>{" "}
              {formatDateTime(currentReport.CreatedAt)}
            </p>
            <Card type="inner" title="问诊报告内容">
              <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {currentReport.PatientReport}
              </pre>
            </Card>
            <Card type="inner" title="对话历史" style={{ marginTop: "16px" }}>
              <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {currentReport.ChatHistory ? 
                  currentReport.ChatHistory.split("\n\n").map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))
                  : '无对话历史'
                }
              </pre>
            </Card>
          </Card>
        )}
      </Modal>
    </Layout>
  );
};

export default DoctorPage;
